import { SvgIcon } from '@cinch-labs/design-system'
import cx from 'classnames'

import {
  getShapeStyles,
  getVariantStyles,
  sizeStyles,
  styles,
} from './badge.styles'
import { BadgeProps } from './badge.types'

/**
 * Badges are labels that need extra prominence or semantic colour. They are
 * read, not operated — a badge is never interactive, so anything clickable
 * should be a Button or a link instead.
 *
 * [View in Figma](https://www.figma.com/design/uoYtLe77tOoT3iN5hpDfv5/DS-Global-V3?node-id=10111-5086)
 *
 * ## Usage
 *
 * Use a badge to label a status, a category, or a small piece of metadata
 * attached to something else on the page.
 *
 * ```tsx
 * import { Badge } from '@cinch-nx/feature-servicing'
 *
 * export const MyComponent = () => (
 *   <Badge text="New to order" variant="highlight" size="large" />
 * )
 * ```
 *
 * ## Best Practices
 *
 * **Do**:
 * - Keep the label to one or two words. Badges do not wrap.
 * - Use the semantic variants (`info`, `error`, `alert`, `success`) only for
 *   their semantic meaning, so colour stays meaningful across the product.
 * - Make sure the label carries the meaning on its own — the icon is
 *   decorative and hidden from assistive technology.
 * - Match the size to the surrounding type rather than to the emphasis you
 *   want; use the variant for emphasis.
 *
 * **Don't**:
 * - Put a badge on something clickable and expect it to be the click target.
 * - Rely on colour alone to distinguish two badges that mean different things.
 * - Use `disabled` to express a disabled control — badges have no state, it is
 *   only a muted appearance.
 * - Stack more than a couple of badges together; they stop reading as labels.
 */
export function Badge(props: BadgeProps) {
  const {
    text,
    className = '',
    variant = 'highlight',
    size = 'm',
    shape = 'pill',
    icon,
    iconPosition = 'left',
    ...htmlProps
  } = props

  if (!text && !icon) {
    return null
  }

  return (
    <span
      className={cx(
        styles.base,
        sizeStyles[size],
        getVariantStyles(variant),
        getShapeStyles(shape),
        className,
      )}
      {...htmlProps}
    >
      {icon && (
        <span className={styles.icon(iconPosition)} aria-hidden="true">
          {/* The nominal size is overridden by --badge-icon-size in the CSS. */}
          <SvgIcon name={icon} size={24} aria-hidden="true" focusable="false" />
        </span>
      )}
      {text}
    </span>
  )
}

export default Badge
